import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { query } from 'urlcat';

interface ExportQueryParams {
  search_query: string;
  page: number;
  search_by: string;
  order_by: string;
  direction: string;
  category_2: string;
  status: string;
  start_date: number | null;
  end_date: number | null;
  distribution_channel: string;
  cg_1_business_segmentation: string[];
  size: number;
}

interface ExportResponse {
  data: Blob;
  filename: string;
}

const exportActivities = async (
  token: string,
  queryParams: ExportQueryParams
): Promise<ExportResponse> => {
  const response = await apiInstance(token).get('/backoffice/order/export', {
    params: cleanDeep(queryParams),
    paramsSerializer: (params) => {
      return query(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type':
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    responseType: 'blob',
  });

  const filename =
    response.headers['content-disposition']?.split('filename=')[1] ||
    'activities-export.xlsx';

  return {
    data: response.data,
    filename,
  };
};

const downloadFile = (blob: Blob, filename: string): void => {
  const windowUrl = window.URL || window.webkitURL;
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const objectUrl = windowUrl.createObjectURL(
    new Blob([blob], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
  );

  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();

  document.body.removeChild(anchor);
  windowUrl.revokeObjectURL(objectUrl);
};

export const useActivityExport = () => {
  const token = useAuthStore((s) => s.access_token);

  const exportMutation = useMutation({
    mutationFn: (queryParams: ExportQueryParams) =>
      exportActivities(token, queryParams),
    onSuccess: (data) => {
      downloadFile(data.data, data.filename);
    },
    onError: (error) => {
      // Error is handled by the component using the mutation
      console.error('Export error:', error);
    },
  });

  return {
    exportActivities: exportMutation.mutateAsync,
    isExporting: exportMutation.isPending,
    exportError: exportMutation.error,
  };
};
